import React from "react";
import MessageCTAs from "./MessageCTAs";
import EnhancedMessageContent from "./EnhancedMessageContent";
import InteractiveMessage from "./InteractiveMessage";
import MessageSources from "./MessageSources";
import WelcomeVideoThumbnail from "./WelcomeVideoThumbnail";
import CoordMessageHeader from "./CoordMessageHeader";
import AssistantLoading from "./AssistantLoading";
import "../../styles/conversation.css";

const MessageList = ({ messages, loading, conversationId, onInteractiveSubmit, isInHjelpestonadMode = false, isWelcomeConversation = false }) => {
  // The welcome message has no persisted marker — it is the first AI message
  // of the welcome conversation.
  const firstAiMessageIndex = messages.findIndex((m) => m.sender === "ai");
  // Everything not from the user renders as Coord (below), so the speaker
  // header shows on a Coord message whose previous message is the user's
  // (or that opens the conversation) — and on the loading state the same way.
  const isFromCoord = (m) => Boolean(m) && m.sender !== "user";

  return (
    <div className="mb-6 flex flex-col space-y-6">
      {messages.map((message, index) => {
        const isUser = message.sender === "user";
        return (
          <div
            key={index}
            className={`flex ${isUser ? "justify-end" : "justify-start"} animate-slide-up`}
          >
            <div
              className={`transition-all duration-200 ${
                isUser
                  ? "p-6 rounded-xl shadow-sm bg-primary-200 text-text-primary max-w-[70%] hover:shadow-md"   // user bubble with lighter peach
                  : "text-text-primary w-full relative"  // AI message without bubble styling
              }`}
            >
              {isUser ? (
                // Plain user text
                <p className="whitespace-pre-wrap text-text-primary leading-relaxed">{message.content}</p>
              ) : (
                // AI message with potential citations and interactive elements
                <div className="relative ai-message-container">
                  {!isFromCoord(messages[index - 1]) && <CoordMessageHeader />}

                  {/* Visual separator for hjelpestønad intro */}
                  {message.type === 'hjelpestonad_intro' && (
                    <div className="mb-6 pb-6 border-b-2 border-primary flex items-center gap-3">
                      <div className="flex-1 h-px bg-primary"></div>
                      <span className="text-primary font-semibold text-lg px-4 whitespace-nowrap">
                        ✨ Velkommen til hjelpestønad veilederen
                      </span>
                      <div className="flex-1 h-px bg-primary"></div>
                    </div>
                  )}

                  {/* Intro video above the welcome message text */}
                  {isWelcomeConversation && index === firstAiMessageIndex && (
                    <WelcomeVideoThumbnail className="mb-4" />
                  )}

                  {/* Enhanced AI message content */}
                  <EnhancedMessageContent
                    content={message.content}
                    messageType={message.type}
                  />

                  {/* Interactive message (checkboxes) if type is 'interactive' */}
                  {message.type === 'interactive' && message.interactiveData && (
                    <InteractiveMessage
                      interactiveData={message.interactiveData}
                      onSubmit={onInteractiveSubmit}
                      disabled={false}
                    />
                  )}

                  {/* Sources: "Vis kilder (n)" opens the sources panel */}
                  <MessageSources citations={message.citations} />

                  {/* CTAs */}
                  <MessageCTAs ctas={message.ctas} conversationId={conversationId} isInHjelpestonadMode={isInHjelpestonadMode} />
                </div>
              )}

              {message.timestamp && (
                <span className={`block text-xs mt-3 ${isUser ? 'text-text-secondary' : 'text-text-detail'}`}>
                  {new Date(message.timestamp).toLocaleTimeString("nb-NO", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {loading && (
        <AssistantLoading showHeader={!isFromCoord(messages[messages.length - 1])} />
      )}
    </div>
  );
};

export default MessageList;
